import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { ErrorBox } from "../support/ErrorBox";
import FormControl from "@mui/material/FormControl";
import { FormikProps } from "formik";
import Grid from "@mui/material/Unstable_Grid2";
import InputLabel from "@mui/material/InputLabel";
import { RequestReason } from "../sharedTypes";
import SelectableCardList from "../../selectcards/SelectableCardList";
import TextField from "@mui/material/TextField";
import { RequestedReasonData as Type } from "../requested-reason-model";
import Typography from "@mui/material/Typography";
import { useTranslation } from "next-i18next";

interface Props {
  reasonForRequestData: RequestReason[];
  selectedReasons: RequestReason[];
  setSelectedReasons: any;
  formik: FormikProps<Type>;
}

/**
 * RequestReason step / screen.
 */
export function CustomerRequestReasonStep({
  reasonForRequestData,
  selectedReasons,
  setSelectedReasons,
  formik,
}: Props) {
  const { t } = useTranslation();

  const reasons: RequestReason[] = reasonForRequestData.map((e) => {
    return { reason: e.reason, reasonEnglishKey: e.reasonEnglishKey };
  });
  const hrRequestKeywords = getKeyWordsHRValidation();

  /**
   * Get key words to check if it is a HR request
   */
  function getKeyWordsHRValidation() {
    const keywordsString = t(
      "request_access:more_information.key_words_hr_request"
    );
    let keywordsList = [];
    if (keywordsString.includes("、")) {
      keywordsList = keywordsString.split("、"); // to handle japanese and chinese
    } else {
      keywordsList = keywordsString.split(",");
    }
    return keywordsList;
  }

  const onChangeList = (list: RequestReason[]) => {
    if (list && list.length > 0) {
      formik.setFieldValue("requestReasons", list);
      if (list.find((e) => e.reasonEnglishKey === "Other")) {
        formik.setFieldValue("showOther", true);
      } else {
        formik.setFieldValue("showOther", false);
        formik.setFieldValue("otherReason", " ");
      }
    } else {
      formik.setFieldValue("requestReasons", []);
      formik.setFieldValue("otherReason", "");
      formik.setFieldValue("showOther", false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          marginBottom: "20px",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: "1.75em",
            fontWeight: "bold",
          }}
        >
          {t("request_access:more_information.reason_for_request")}
        </Typography>
      </Box>
      <Box sx={{ mt: "24px", mb: "12px", textAlign: "left" }}>
        {t("request_access:customer_access.reason_for_request_summary")}
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ flexGrow: 1 }}>
          <Grid
            alignItems="stretch"
            container
            rowSpacing={4}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            <SelectableCardList
              list={reasons}
              changeHandler={onChangeList}
              selectedItems={selectedReasons}
              setSelectedItems={setSelectedReasons}
            />
          </Grid>
        </Box>

        {/* formik.values.requestReason == other */}
        {formik.values.showOther && (
          <Box sx={{ textAlign: "left" }}>
            <InputLabel
              sx={{ textAlign: "left", mt: "1rem" }}
              htmlFor="cargillBusiness"
            >
              {t("request_access:more_information.explanation")}
            </InputLabel>
            <FormControl fullWidth>
              <TextField
                fullWidth
                multiline
                rows={4}
                id="otherReason"
                name="otherReason"
                value={formik.values.otherReason}
                placeholder={
                  t("request_access:more_information.tell_us_why") || ""
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </FormControl>
            {formik.errors.otherReason && formik.touched.otherReason && (
              <ErrorBox>{formik.errors.otherReason}</ErrorBox>
            )}
            {hrRequestKeywords.some((el) =>
              formik.values.otherReason?.toLowerCase().includes(el)
            ) && (
              <Box
                sx={{
                  backgroundColor: "hsla(0,0%,60%,.2)",
                  border: "1.5px solid #999",
                  p: 4,
                  mb: "28px",
                  mt: "28px",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      t("request_access:more_information.go_to_myhr") || "",
                  }}
                ></div>
              </Box>
            )}
          </Box>
        )}

        <Box mt={12}>
          <Button
            sx={{ width: "100%", mb: 4 }}
            type="submit"
            disabled={!(formik.dirty && formik.isValid)}
          >
            {t("common:common.continue")}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
